/**
 * W-10 收银台(WScreen-10)。视觉真源 design/cashier.html。
 *
 * ★关键流程(epic 20-4 AC + tech-plan §2/§31,drift 于 web-pages/cashier.md「乐观跳转」):
 *   点「确认支付」→ payOrder(orderId, MOCK)(MVP method=MOCK;UI 呈现微信支付、不露 mock)
 *   → 进「支付确认中…」态 → 轮询 GET /orders/{id} 直到 state===IN_PROGRESS(mock 5s 自动回调)
 *   → 跳 W-11 支付成功。
 * ★method:UI 微信支付默选,线上送 'MOCK'(支付通道路由=后端关切,openapi:MVP 仅 MOCK)。
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getOrderDetail, payOrder } from '@/api/order';
import { errorCode, errorMessage, formatAmount } from '@/lib/orderState';
import { formatMmSs, useCountdown } from '@/lib/useCountdown';

interface CashierState {
  orderId?: string;
  amount?: number;
  expiresAt?: string;
  sellerNickname?: string;
  sellerAvatar?: string | null;
}

/** 轮询节流与兜底上限。 */
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 25_000;

function mapPayError(err: unknown): string {
  switch (errorCode(err)) {
    case '030301':
      return '订单不存在';
    case '030302':
      return '订单不属于当前用户';
    case '030401':
      return '订单当前状态不允许支付';
    case '030402':
      return '订单已超时';
    case '030403':
      return '支付方式暂不可用';
    case '010100':
      return '登录已过期,请重新登录';
    case 'NETWORK_ERROR':
      return '网络异常,请重试';
    default:
      return errorMessage(err, '支付发起失败');
  }
}

type PayPhase = 'idle' | 'paying' | 'confirming';

export function CashierPage() {
  const navigate = useNavigate();
  const { orderId: routeOrderId } = useParams<{ orderId: string }>();
  const routeState = (useLocation().state as CashierState | null) ?? {};
  const orderId = routeState.orderId ?? routeOrderId ?? '';

  // 直链/刷新丢 state:兜底拉订单补 amount/expiresAt(无 expiresAt 精确值则近似)。
  const [amount, setAmount] = useState<number | undefined>(routeState.amount);
  const [expiresAt, setExpiresAt] = useState<string | undefined>(routeState.expiresAt);
  // 无 route state 的直链需先拉订单再定展示;bootstrapping 期显加载,避免闪「已超时」。
  const [bootstrapping, setBootstrapping] = useState(routeState.amount == null && !!orderId);
  const [phase, setPhase] = useState<PayPhase>('idle');
  const [payError, setPayError] = useState<string | null>(null);
  const payingRef = useRef(false);
  // 卸载守卫:放弃支付/后退离屏后,轮询循环不得再 setState / navigate。
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const countdown = useCountdown(expiresAt);

  useEffect(() => {
    if (amount != null || !orderId) return;
    let cancelled = false;
    getOrderDetail(orderId)
      .then((d) => {
        if (cancelled) return;
        setAmount(d.amount);
        // 直链丢 expiresAt:按 createdAt + 15min 近似支付窗(state-machine §五)。
        if (!expiresAt && d.createdAt) {
          const ts = Date.parse(d.createdAt);
          if (!Number.isNaN(ts)) setExpiresAt(new Date(ts + 15 * 60_000).toISOString());
        }
        // 已支付(IN_PROGRESS)/已完成(COMPLETED)→ 直接进成功页,不对终态订单再收银。
        if (d.state === 'IN_PROGRESS' || d.state === 'COMPLETED') {
          navigate(`/payment-success/${orderId}`, {
            replace: true,
            state: { orderId, amount: d.amount, sellerNickname: routeState.sellerNickname },
          });
        }
      })
      .catch(() => {
        /* 拉取失败:保持无金额态,由 CTA 错误兜底 */
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const pollUntilPaid = async (): Promise<'paid' | 'cancelled' | 'timeout'> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    // 立即查一次,再按间隔轮询,直到 IN_PROGRESS / 终态 / 超时;离屏即停(防泄漏)。
    for (;;) {
      if (!mountedRef.current) return 'timeout';
      try {
        const detail = await getOrderDetail(orderId);
        if (detail.state === 'IN_PROGRESS' || detail.state === 'COMPLETED') return 'paid';
        if (detail.state === 'CANCELLED') return 'cancelled';
      } catch {
        /* 轮询期瞬时错误:继续重试到超时 */
      }
      if (Date.now() >= deadline) return 'timeout';
      await sleep(POLL_INTERVAL_MS);
    }
  };

  const handlePay = async () => {
    if (payingRef.current || countdown.isExpired || !orderId) return;
    payingRef.current = true;
    setPhase('paying');
    setPayError(null);
    try {
      // MVP:线上送 MOCK(UI 仍微信支付);后端 MOCK 返 deep-link 不唤起。
      await payOrder(orderId, 'MOCK');
      if (!mountedRef.current) return; // 已离屏(放弃支付/后退),停止
      setPhase('confirming');
      const result = await pollUntilPaid();
      if (!mountedRef.current) return; // 轮询期间离屏:不 setState / navigate
      if (result === 'paid') {
        navigate(`/payment-success/${orderId}`, {
          state: {
            orderId,
            amount,
            sellerNickname: routeState.sellerNickname,
            sellerAvatar: routeState.sellerAvatar,
          },
        });
        return; // 离屏,不复位
      }
      setPayError(
        result === 'cancelled'
          ? '订单已取消,请返回重新下单'
          : '支付确认超时,请在订单中查看支付状态',
      );
      setPhase('idle');
      payingRef.current = false;
    } catch (err) {
      if (!mountedRef.current) return;
      setPayError(mapPayError(err));
      setPhase('idle');
      payingRef.current = false;
    }
  };

  if (bootstrapping) {
    return (
      <div className="inner section">
        <div className="state" data-testid="cashier-loading">
          <p className="muted sm" style={{ color: 'var(--color-primary)' }}>
            加载中…
          </p>
        </div>
      </div>
    );
  }

  const isExpired = countdown.isExpired;
  const payDisabled = phase !== 'idle' || isExpired;
  const amountLabel = amount != null ? formatAmount(amount) : '—';

  return (
    <div className="inner section">
      <h1 className="h2 mb-6">收银台</h1>

      <div className="checkout-cols">
        {/* 左:支付方式 + 订单信息 */}
        <div>
          <div className="card card-pad mb-6">
            <div className="row between mb-4">
              <h3 className="h3">选择支付方式</h3>
              <span className="sm muted">
                ⏳ 支付剩余{' '}
                <span
                  className="price"
                  style={{
                    fontSize: 'var(--text-base-size)',
                    color: isExpired
                      ? 'var(--color-destructive)'
                      : countdown.secondsLeft < 60
                        ? 'var(--color-primary)'
                        : undefined,
                  }}
                >
                  {isExpired ? '已超时' : formatMmSs(countdown.secondsLeft)}
                </span>
              </span>
            </div>

            <label
              className="list-item"
              style={{
                cursor: 'default',
                border: '1px solid var(--color-primary)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-3)',
              }}
            >
              <span className="avatar" style={{ background: '#1aad19', color: '#fff' }}>
                微
              </span>
              <div className="col" style={{ flex: 1, gap: 2 }}>
                <span style={{ fontWeight: 'var(--weight-medium)' }}>微信支付</span>
                <span className="xs muted">推荐微信用户使用,安全便捷</span>
              </div>
              <span className="badge badge-primary" style={{ flexShrink: 0 }}>
                已选
              </span>
            </label>
          </div>

          <div className="card card-pad">
            <h3 className="h3 mb-4">订单信息</h3>
            <div className="row between mb-3">
              <span className="sm muted">订单编号</span>
              <span className="sm">{orderId || '—'}</span>
            </div>
            <div className="row between">
              <span className="sm muted">卖家</span>
              <span className="sm">{routeState.sellerNickname ?? '—'}</span>
            </div>
          </div>
        </div>

        {/* 右:金额摘要 + 确认支付 */}
        <aside className="sticky-cta">
          <div className="card card-pad">
            <h3 className="h3 mb-4">支付金额</h3>
            <div className="row between mb-2">
              <span style={{ fontWeight: 'var(--weight-semibold)' }}>应付金额</span>
              <span className="price" style={{ fontSize: 'var(--text-3xl-size)' }}>
                {amountLabel}
              </span>
            </div>

            {isExpired ? (
              <p className="sm mb-6" style={{ color: 'var(--color-destructive)' }} role="alert">
                订单已超时,请返回重新下单
              </p>
            ) : (
              <p className="xs muted mb-6">订单 15 分钟内有效,超时将自动关闭。</p>
            )}

            {phase === 'confirming' && (
              <p className="sm pay-confirming mb-3" role="status" data-testid="cashier-confirming">
                支付确认中…请稍候
              </p>
            )}
            {payError && (
              <p
                className="sm mb-3"
                role="alert"
                data-testid="cashier-pay-error"
                style={{ color: 'var(--color-destructive)' }}
              >
                {payError}
              </p>
            )}

            <button
              type="button"
              className="btn btn-primary btn-lg btn-block mb-3"
              disabled={payDisabled}
              onClick={handlePay}
            >
              {phase === 'paying'
                ? '发起支付…'
                : phase === 'confirming'
                  ? '支付确认中…'
                  : `确认支付 ${amountLabel}`}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => navigate('/orders')}
            >
              放弃支付
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
